import React, { useEffect, useState, useRef, Children } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Lock, Mail, User as UserIcon, Eye, EyeOff, AlertCircle, ShoppingBag, Plus, ChevronDown } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { shopData } from './shopData';
import { bookData } from './bookData';
import { createCheckoutSession, redirectToCheckout } from '../../../utils/stripe';
import type { CartItem } from '../../../contexts/CartContext';
import type { CheckoutLineItemInput } from '../../../utils/stripe';
// Resolve the product image for a cart item (supplement image or book cover).
function getCartItemImage(item: CartItem): string | undefined {
  const name = item.name?.trim().toLowerCase();
  if (item.type === 'book') {
    const b = bookData.find(x => x.id === item.productId) ?? bookData.find(x => x.title?.trim().toLowerCase() === name);
    return b?.coverImage;
  }
  const p = shopData.find(s => s.id === item.productId) ?? shopData.find(s => s.name?.trim().toLowerCase() === name);
  return p?.image;
}
function PaymentMethod() {
  const [expanded, setExpanded] = useState(false);
  return <div className="w-full" data-id="element-1781">
      <div className="bg-white border border-[#E5E5E5] shadow-sm rounded-xl overflow-hidden" data-id="element-1782">
        {!expanded ? <div className="p-5" data-id="element-1783">
            <div className="flex items-center justify-between mb-4" data-id="element-1784">
              <p className="font-bold text-[#111111] text-lg" data-id="element-1785">
                Paying with Visa 3038
              </p>
              <button type="button" onClick={() => setExpanded(true)} className="text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1786">
                Change
              </button>
            </div>
            <div className="space-y-3" data-id="element-1787">
              <button className="block text-sm text-tealslate hover:text-navyslate transition-colors" data-id="element-1788">
                Select a payment plan
              </button>
              <button className="block text-sm text-tealslate hover:text-navyslate transition-colors" data-id="element-1789">
                Use a gift card, voucher, or promo code
              </button>
            </div>
          </div> : <div className="p-5 md:p-6 bg-[#F9F9F9]" data-id="element-1790">
            <h3 className="font-bold text-[#111111] mb-4" data-id="element-1791">Add new card</h3>
            <div className="space-y-4" data-id="element-1792">
              <div data-id="element-1793">
                <label className="block text-xs font-bold text-[#111111] mb-1.5 uppercase tracking-wider" data-id="element-1794">
                  Card Number
                </label>
                <input type="text" placeholder="0000 0000 0000 0000" className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" data-id="element-1795" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1796">
                <div data-id="element-1797">
                  <label className="block text-xs font-bold text-[#111111] mb-1.5 uppercase tracking-wider" data-id="element-1798">
                    Expiry
                  </label>
                  <input type="text" placeholder="MM/YY" className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" data-id="element-1799" />
                </div>
                <div data-id="element-1800">
                  <label className="block text-xs font-bold text-[#111111] mb-1.5 uppercase tracking-wider" data-id="element-1801">
                    CVC
                  </label>
                  <input type="text" placeholder="123" className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" data-id="element-1802" />
                </div>
              </div>
              <div data-id="element-1803">
                <label className="block text-xs font-bold text-[#111111] mb-1.5 uppercase tracking-wider" data-id="element-1804">
                  Name on Card
                </label>
                <input type="text" placeholder="Jack Achenbach" className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all rounded-md shadow-sm" data-id="element-1805" />
              </div>
              <div className="flex items-center gap-3 pt-2" data-id="element-1806">
                <button type="button" onClick={() => setExpanded(false)} className="px-6 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm" data-id="element-1807">
                  Save Card
                </button>
                <button type="button" onClick={() => setExpanded(false)} className="px-6 py-3 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm" data-id="element-1808">
                  Cancel
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}
// Build a Stripe-compatible line item reference from a cart item.
// Priority: 1) lookupKey on the item, 2) stripePriceId on the item,
//           3) shopData lookup by lookupKey (supplements), 4) null.
function getCheckoutRefFor(item: CartItem): Pick<CheckoutLineItemInput, 'priceId' | 'lookupKey'> | null {
  if (item.lookupKey) return {
    lookupKey: item.lookupKey
  };
  if (item.stripePriceId) return {
    priceId: item.stripePriceId
  };
  const supplement = shopData.find(p => p.id === item.productId);
  if (supplement?.lookupKey) return {
    lookupKey: supplement.lookupKey
  };
  return null;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Sign in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
type Mode = 'signin' | 'signup';
export function Checkout() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    signIn,
    signUp
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [mode, setMode] = useState<Mode>('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Empty cart guard
  if (!authLoading && items.length === 0) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex flex-col items-center justify-center pt-32 pb-20 px-6" data-id="element-1809">
        <div className="max-w-2xl mx-auto text-center" data-id="element-1810">
          <div className="w-20 h-20 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1811">
            <ShoppingBag className="w-8 h-8 text-[#9A9A90]" data-id="element-1812" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-4" data-id="element-1813">
            Your cart is empty.
          </h1>
          <p className="text-sm font-medium text-[#5A5A5A] mb-10" data-id="element-1814">
            Add a book or supplement to begin checkout.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1815">
            <Link to="/books" className="px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1816">
              Browse Books <ArrowRight className="w-4 h-4" data-id="element-1817" />
            </Link>
            <Link to="/shop" className="px-8 py-4 bg-white border border-[#E5E5E5] text-[#111111] hover:border-tealslate hover:text-tealslate text-sm font-bold transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-1818">
              Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1819" />
            </Link>
          </div>
        </div>
      </div>;
  }
  // Auth gate passed: show the payment step
  if (user && !authLoading) {
    return <PaymentStep data-id="element-1820" />;
  }
  // While auth is resolving, show a quiet loading state
  if (authLoading) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center" data-id="element-1821">
        <span className="w-8 h-8 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1822" />
      </div>;
  }
  // Auth gate form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name.');
          setSubmitting(false);
          return;
        }
        await signUp(email, password, name.trim(), marketingOptIn);
      } else {
        await signIn(email, password);
      }
      // Stay on /checkout — user is now signed in, the if(user) branch above renders
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-1823">
      <section className="pt-32 pb-8 px-6" data-id="element-1824">
        <div className="max-w-5xl mx-auto" data-id="element-1825">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1826">
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-2" data-id="element-1827">
              {mode === 'signup' ? 'Create an account to continue.' : 'Sign in to continue.'}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A]" data-id="element-1828">
              An account lets you track orders, access digital downloads, and
              save info for next time.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 px-6" data-id="element-1829">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12" data-id="element-1830">
          {/* Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-3" data-id="element-1831">
            {/* Mode toggle */}
            <div className="flex bg-white border border-[#E5E5E5] rounded-md p-1 mb-8 shadow-sm" data-id="element-1832">
              <button type="button" onClick={() => {
              setMode('signup');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signup' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1833">
                New Customer
              </button>
              <button type="button" onClick={() => {
              setMode('signin');
              setError(null);
            }} className={`flex-1 py-3 text-sm font-bold rounded transition-colors ${mode === 'signin' ? 'bg-tealslate text-white shadow-sm' : 'text-[#5A5A5A] hover:text-[#111111]'}`} data-id="element-1834">
                Returning Customer
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1835">
              {mode === 'signup' && <div data-id="element-1836">
                  <label htmlFor="name" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1837">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-1838">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1839" />
                    <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="Jane Smith" data-id="element-1840" />
                  </div>
                </div>}

              <div data-id="element-1841">
                <label htmlFor="email" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1842">
                  Email
                </label>
                <div className="relative" data-id="element-1843">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1844" />
                  <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-1845" />
                </div>
              </div>

              <div data-id="element-1846">
                <label htmlFor="password" className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1847">
                  Password
                </label>
                <div className="relative" data-id="element-1848">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1849" />
                  <input id="password" type={showPassword ? 'text' : 'password'} required minLength={mode === 'signup' ? 6 : undefined} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm pl-11 pr-12 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'} data-id="element-1850" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-tealslate transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-1851">
                    {showPassword ? <EyeOff className="w-4 h-4" data-id="element-1852" /> : <Eye className="w-4 h-4" data-id="element-1853" />}
                  </button>
                </div>
                {mode === 'signin' && <div className="mt-3 text-right" data-id="element-1854">
                    <Link to="/forgot-password" className="text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1855">
                      Forgot password?
                    </Link>
                  </div>}
              </div>

              {mode === 'signup' && <label className="flex items-start gap-3 cursor-pointer pt-2" data-id="element-1856">
                  <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-1857" />
                  <span className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1858">
                    Send me updates on new books, supplements, and articles.
                  </span>
                </label>}

              {error && <div className="bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-1859">
                  <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1860" />
                  <p className="text-sm text-burgundy" data-id="element-1861">{error}</p>
                </div>}

              <button type="submit" disabled={submitting} className="w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2 mt-4" data-id="element-1862">
                {submitting ? <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1863" />
                    {mode === 'signup' ? 'Creating account…' : 'Signing in…'}
                  </> : <>
                    {mode === 'signup' ? 'Create Account & Continue' : 'Sign In & Continue'}
                    <ArrowRight className="w-4 h-4" data-id="element-1864" />
                  </>}
              </button>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-2" data-id="element-1865">
            <CartSummary data-id="element-1866" />
          </motion.aside>
        </div>
      </section>
    </div>;
}
// PRE-ORDER MODE — Formspree endpoint receives pre-order notifications.
// Same endpoint as the contact form so submissions land in one inbox.
const PREORDER_FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
function PaymentStep() {
  const navigate = useNavigate();
  const {
    user,
    profile,
    updateProfile
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedAddresses = profile?.addresses ?? [];
  const defaultAddress = savedAddresses.find(a => a.isDefault) ?? savedAddresses[0];
  const [selectedAddressId, setSelectedAddressId] = useState<string>(defaultAddress ? defaultAddress.id : 'new');
  // Re-sync the selection once saved addresses load. When the user signs in
  // directly on /checkout, the profile (and its addresses) arrives AFTER this
  // component mounts, so the initial state falls back to 'new'. Whenever a
  // default address becomes available but the current selection is 'new' or no
  // longer matches a saved address, snap to the default so the saved address is
  // pre-selected instead of showing the blank manual form.
  useEffect(() => {
    if (!defaultAddress) return;
    const selectionIsValid = savedAddresses.some(a => a.id === selectedAddressId);
    if (!selectionIsValid) {
      setSelectedAddressId(defaultAddress.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses.length, defaultAddress?.id]);
  const [saveNewAddress, setSaveNewAddress] = useState(true);
  // Shipping Address Form State
  const [recipientName, setRecipientName] = useState(user?.displayName ?? '');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  // Whether the address picker is expanded to show all saved addresses.
  const [expanded, setExpanded] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const selectedAddr = savedAddresses.find(a => a.id === selectedAddressId);
  const unbuyable = items.filter(i => !getCheckoutRefFor(i));
  // Load the selected address's saved delivery instructions whenever the
  // selected address changes (so each address keeps its own note).
  useEffect(() => {
    const addr = savedAddresses.find(a => a.id === selectedAddressId);
    setDeliveryInstructions(addr?.deliveryInstructions ?? '');
    setShowInstructions(!!addr?.deliveryInstructions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddressId]);
  // Auto-save delivery instructions onto the specific selected address.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistInstructions = (value: string) => {
    if (!selectedAddr) return;
    const updated = savedAddresses.map(a => a.id === selectedAddr.id ? {
      ...a,
      deliveryInstructions: value.trim()
    } : a);
    updateProfile({
      addresses: updated
    });
  };
  const handleInstructionsChange = (value: string) => {
    setDeliveryInstructions(value);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistInstructions(value), 700);
  };
  // Flush any pending save on unmount.
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);
  // PRE-ORDER MODE — Real Stripe checkout kept for later launch.
  // To restore: delete handlePreOrder below and the Pre-Order button,
  // then uncomment the original "Pay with Stripe" button in the JSX.
  // Note: The shipping address state collected here is available to pass to Stripe
  // or save to the user's account when the real checkout path is restored.
  const handleCheckout = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const lineItems = items.map(i => {
        const ref = getCheckoutRefFor(i);
        if (!ref) return null;
        return {
          ...ref,
          quantity: i.quantity
        };
      }).filter((x): x is NonNullable<typeof x> => x !== null);
      if (lineItems.length === 0) {
        setError('No items in your cart are available for purchase yet.');
        setSubmitting(false);
        return;
      }
      const origin = window.location.origin;
      const {
        url
      } = await createCheckoutSession({
        lineItems,
        successUrl: origin + '/account?order=success',
        cancelUrl: origin + '/checkout'
      });
      redirectToCheckout(url);
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong starting checkout.');
      setSubmitting(false);
    }
  };
  // Save a brand-new address to the profile right here on the checkout
  // screen, then select it and collapse the picker.
  const handleSaveNewAddress = async () => {
    if (!recipientName.trim() || !addressLine1.trim() || !city.trim() || !stateProvince.trim() || !zip.trim() || !country.trim()) {
      setError('Please fill out all required shipping address fields.');
      return;
    }
    setSavingAddr(true);
    setError(null);
    try {
      const newAddress = {
        id: Math.random().toString(36).substring(2, 9),
        recipientName: recipientName.trim(),
        line1: addressLine1.trim(),
        line2: addressLine2.trim(),
        city: city.trim(),
        state: stateProvince.trim(),
        postalCode: zip.trim(),
        country: country.trim(),
        isDefault: savedAddresses.length === 0
      };
      await updateProfile({
        addresses: [...savedAddresses, newAddress]
      });
      setSelectedAddressId(newAddress.id);
      setExpanded(false);
      setAddressLine1('');
      setAddressLine2('');
      setCity('');
      setStateProvince('');
      setZip('');
    } catch (err: any) {
      setError(err?.message ?? 'Could not save this address. Please try again.');
    } finally {
      setSavingAddr(false);
    }
  };
  const handlePreOrder = async () => {
    setError(null);
    let finalAddress = '';
    if (selectedAddressId === 'new') {
      // Validate shipping address
      if (!recipientName.trim() || !addressLine1.trim() || !city.trim() || !stateProvince.trim() || !zip.trim() || !country.trim()) {
        setError('Please fill out all required shipping address fields.');
        return;
      }
      finalAddress = `${recipientName}\n${addressLine1}${addressLine2 ? '\n' + addressLine2 : ''}\n${city}, ${stateProvince} ${zip}\n${country}`;
      if (saveNewAddress) {
        const newAddress = {
          id: Math.random().toString(36).substring(2, 9),
          recipientName: recipientName.trim(),
          line1: addressLine1.trim(),
          line2: addressLine2.trim(),
          city: city.trim(),
          state: stateProvince.trim(),
          postalCode: zip.trim(),
          country: country.trim(),
          isDefault: savedAddresses.length === 0 // Make default if it's the first one
        };
        await updateProfile({
          addresses: [...savedAddresses, newAddress]
        });
      }
    } else {
      const addr = savedAddresses.find(a => a.id === selectedAddressId);
      if (!addr) {
        setError('Selected address not found.');
        return;
      }
      finalAddress = `${addr.recipientName}\n${addr.line1}${addr.line2 ? '\n' + addr.line2 : ''}\n${addr.city}, ${addr.state} ${addr.postalCode}\n${addr.country}`;
    }
    if (deliveryInstructions.trim()) {
      finalAddress += `\n\nDelivery Instructions:\n${deliveryInstructions.trim()}`;
    }
    setSubmitting(true);
    try {
      const itemsList = items.map(i => `• ${i.name} × ${i.quantity} — ${formatPrice(i.unitPrice * i.quantity)}`).join('\n');
      const formattedAddress = finalAddress;
      const formData = new FormData();
      formData.append('Customer Email', user?.email ?? '');
      formData.append('Customer Name', user?.displayName ?? '');
      formData.append('_subject', 'Pre-Order Received - Structured Nutrition');
      formData.append('Shipping Address', formattedAddress);
      formData.append('_autoresponse', `Hi${user?.displayName ? ' ' + user.displayName.split(' ')[0] : ''},\n\nThanks for your pre-order! Your interest helps us plan our first production run. We'll notify you by email when these items are ready for purchase so you can complete your order.\n\nYour reserved items:\n${itemsList}\n\nNo payment has been taken — your cart is saved for when we go live.\n\n— Jack & the Structured Nutrition team`);
      const response = await fetch(PREORDER_FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Could not submit your pre-order. Please try again.');
      }
      // Cart intentionally NOT cleared — items stay so user can return.
      navigate('/pre-order-confirmation');
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong submitting your pre-order.');
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-1867">
      <section className="pt-32 pb-24 px-6" data-id="element-1868">
        <div className="max-w-5xl mx-auto flex flex-col gap-6" data-id="element-1869">
          <div className="w-full" data-id="element-1870">
            <h2 className="text-2xl font-bold text-[#111111] mb-4 tracking-tight" data-id="element-1871">
              Shipping Address
            </h2>

            <div className="bg-white border border-[#E5E5E5] shadow-sm rounded-xl overflow-hidden" data-id="element-1872">
              {/* COLLAPSED — only the selected address with a subtle Change action */}
              {selectedAddressId !== 'new' && !expanded && selectedAddr && <div className="p-5" data-id="element-1873">
                  <div className="flex items-start gap-4" data-id="element-1874">
                    <div className="pt-1" data-id="element-1875">
                      <span className="block w-4 h-4 rounded-full border-2 border-tealslate flex items-center justify-center" data-id="element-1876">
                        <span className="block w-2 h-2 rounded-full bg-tealslate" data-id="element-1877" />
                      </span>
                    </div>
                    <div className="flex-1 flex items-start justify-between gap-4" data-id="element-1878">
                      <div data-id="element-1879">
                        <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-1880">
                          {selectedAddr.recipientName}
                          {selectedAddr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-1881">
                              Default
                            </span>}
                        </p>
                        <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1882">
                          {selectedAddr.line1}
                          {selectedAddr.line2 && <br data-id="element-1883" />}
                          {selectedAddr.line2}
                          <br data-id="element-1884" />
                          {selectedAddr.city}, {selectedAddr.state}{' '}
                          {selectedAddr.postalCode}
                          <br data-id="element-1885" />
                          {selectedAddr.country}
                        </p>
                      </div>
                      <button type="button" onClick={() => setExpanded(true)} className="shrink-0 text-sm font-bold text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1886">
                        Change
                      </button>
                    </div>
                  </div>

                  {/* Delivery instructions — click the text to open/close the box */}
                  <div className="mt-4 pl-8" data-id="element-1887">
                    <button type="button" onClick={() => setShowInstructions(v => !v)} aria-expanded={showInstructions} className="inline-flex items-center gap-1.5 text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1888">
                      {showInstructions ? <ChevronDown className="w-4 h-4" data-id="element-1889" /> : <Plus className="w-4 h-4" data-id="element-1890" />}
                      {showInstructions ? 'Delivery instructions' : 'Add delivery instructions'}
                    </button>
                    <AnimatePresence initial={false} data-id="element-1891">
                      {showInstructions && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.25,
                    ease: [0.2, 0.65, 0.3, 0.9]
                  }} className="overflow-hidden" data-id="element-1892">
                          <textarea autoFocus value={deliveryInstructions} onChange={e => handleInstructionsChange(e.target.value)} onBlur={e => persistInstructions(e.target.value)} placeholder="e.g. Leave at the front door, gate code is 1234…" rows={2} className="mt-2 w-full bg-[#F9F9F9] border border-[#E5E5E5] rounded-md px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-1893" />
                          <p className="mt-1.5 text-xs text-[#9A9A90]" data-id="element-1894">
                            Saved to this address automatically.
                          </p>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </div>}

              {/* EXPANDED — all saved addresses stacked, plus add-new affordance */}
              {selectedAddressId !== 'new' && expanded && <AnimatePresence initial={false} data-id="element-1895">
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} data-id="element-1896">
                    {savedAddresses.length > 0 && <div className="divide-y divide-[#E5E5E5]" data-id="element-1897">
                        {savedAddresses.map(addr => <label key={addr.id} className={`block p-5 cursor-pointer transition-colors ${selectedAddressId === addr.id ? 'bg-[#F9F9F9]' : 'bg-white hover:bg-[#FAFAFA]'}`} data-id="element-1898">
                            <div className="flex items-start gap-4" data-id="element-1899">
                              <div className="pt-1" data-id="element-1900">
                                <input type="radio" name="shippingAddress" value={addr.id} checked={selectedAddressId === addr.id} onChange={() => {
                          setSelectedAddressId(addr.id);
                          setExpanded(false);
                        }} className="w-4 h-4 accent-tealslate" data-id="element-1901" />
                              </div>
                              <div className="flex-1" data-id="element-1902">
                                <p className="font-bold text-[#111111] mb-1 flex items-center gap-2" data-id="element-1903">
                                  {addr.recipientName}
                                  {addr.isDefault && <span className="bg-[#F9F9F9] text-[#5A5A5A] text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-bold" data-id="element-1904">
                                      Default
                                    </span>}
                                </p>
                                <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-1905">
                                  {addr.line1}
                                  {addr.line2 && <br data-id="element-1906" />}
                                  {addr.line2}
                                  <br data-id="element-1907" />
                                  {addr.city}, {addr.state} {addr.postalCode}
                                  <br data-id="element-1908" />
                                  {addr.country}
                                </p>
                              </div>
                            </div>
                          </label>)}
                      </div>}
                    <div className="p-5 border-t border-[#E5E5E5]" data-id="element-1909">
                      <button type="button" onClick={() => {
                    setRecipientName(user?.displayName ?? '');
                    setSelectedAddressId('new');
                  }} className="inline-flex items-center gap-1.5 text-sm font-bold text-tealslate hover:text-navyslate transition-colors" data-id="element-1910">
                        <Plus className="w-4 h-4" data-id="element-1911" />
                        Add new address
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>}

              {selectedAddressId === 'new' && <div className="p-6 md:p-8" data-id="element-1912">
                  <div className="space-y-5" data-id="element-1913">
                    <div data-id="element-1914">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1915">
                        Recipient Full Name *
                      </label>
                      <input type="text" required value={recipientName} onChange={e => setRecipientName(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1916" />
                    </div>
                    <div data-id="element-1917">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1918">
                        Address Line 1 *
                      </label>
                      <input type="text" required value={addressLine1} onChange={e => setAddressLine1(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1919" />
                    </div>
                    <div data-id="element-1920">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1921">
                        Address Line 2 (Optional)
                      </label>
                      <input type="text" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1922" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1923">
                      <div data-id="element-1924">
                        <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1925">
                          City *
                        </label>
                        <input type="text" required value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1926" />
                      </div>
                      <div className="grid grid-cols-2 gap-5" data-id="element-1927">
                        <div data-id="element-1928">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1929">
                            State / Province *
                          </label>
                          <input type="text" required value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1930" />
                        </div>
                        <div data-id="element-1931">
                          <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1932">
                            ZIP / Postal *
                          </label>
                          <input type="text" required value={zip} onChange={e => setZip(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1933" />
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1934">
                      <label className="block text-sm font-bold text-[#111111] mb-2" data-id="element-1935">
                        Country *
                      </label>
                      <input type="text" required value={country} onChange={e => setCountry(e.target.value)} className="w-full bg-white border border-[#E5E5E5] rounded-md shadow-sm px-4 py-3.5 text-[#111111] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-1936" />
                    </div>

                    <div className="flex items-center gap-3 pt-2" data-id="element-1937">
                      <button type="button" onClick={handleSaveNewAddress} disabled={savingAddr} className="px-5 py-3 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm disabled:opacity-60 inline-flex items-center justify-center gap-2" data-id="element-1938">
                        {savingAddr ? <>
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1939" />
                            Saving…
                          </> : 'Save address'}
                      </button>
                      {defaultAddress && <button type="button" onClick={() => {
                    setSelectedAddressId(defaultAddress.id);
                    setExpanded(false);
                  }} className="text-sm font-bold text-[#9A9A90] hover:text-[#111111] transition-colors" data-id="element-1940">
                          Cancel
                        </button>}
                    </div>
                    <p className="text-xs text-[#9A9A90] leading-relaxed" data-id="element-1941">
                      This address will be saved to your account for future
                      orders.
                    </p>
                  </div>
                </div>}
            </div>

            {error && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-1942">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1943" />
                <p className="text-sm text-burgundy" data-id="element-1944">{error}</p>
              </div>}

            {/* PRE-ORDER MODE — Real Stripe pay button hidden until launch.
             To restore: delete the Pre-Order button below and uncomment this one.
             <button
             type="button"
             onClick={handleCheckout}
             disabled={submitting || items.length === 0}
             className="mt-8 w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2"
             >
             {submitting ? (
             <>
             <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
             Redirecting to Stripe…
             </>
             ) : (
             <>
             <Lock className="w-4 h-4" />
             Pay {formatPrice(subtotal)} with Stripe
             </>
             )}
             </button>
             <p className="text-sm text-[#5A5A5A] text-center mt-4">
             Secure payment processed by Stripe. You'll return here when done.
             </p>
             */}
          </div>

          <PaymentMethod data-id="element-1945" />

          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="w-full" data-id="element-1946">
            <CartSummary data-id="element-1947" />

            {unbuyable.length > 0 && <div className="mt-6 bg-burgundy/10 rounded-md p-4 flex gap-3 items-start" data-id="element-1948">
                <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1949" />
                <div className="text-sm text-burgundy" data-id="element-1950">
                  <p className="font-bold mb-1" data-id="element-1951">
                    Some items aren't available for purchase yet:
                  </p>
                  <ul className="list-disc ml-5" data-id="element-1952">
                    {unbuyable.map(i => <li key={i.productId} data-id="element-1953">{i.name}</li>)}
                  </ul>
                </div>
              </div>}
          </motion.aside>

          <div className="w-full" data-id="element-1954">
            <button type="button" onClick={handlePreOrder} disabled={submitting || items.length === 0} className="w-full px-6 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed rounded-md shadow-sm flex items-center justify-center gap-2" data-id="element-1955">
              {submitting ? <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1956" />
                  Submitting pre-order…
                </> : <>
                  Pre-Order <ArrowRight className="w-4 h-4" data-id="element-1957" />
                </>}
            </button>

            <p className="text-sm text-[#5A5A5A] text-center mt-4" data-id="element-1958">
              No payment is taken. Your items stay in your cart so you can
              return when we launch.
            </p>
          </div>
        </div>
      </section>
    </div>;
}
function CartSummary() {
  const {
    items,
    subtotal,
    itemCount
  } = useCart();
  return <div className="bg-white border border-[#E5E5E5]" data-id="element-1959">
      <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between" data-id="element-1960">
        <h2 className="text-xl font-bold text-[#111111] tracking-tight" data-id="element-1961">
          Order Summary
        </h2>
        <span className="text-sm font-medium text-[#9A9A90]" data-id="element-1962">Price</span>
      </div>
      <ul data-id="element-1963">
        {items.map(item => {
        const image = getCartItemImage(item);
        return <li key={item.productId} className="px-6 py-5 border-b border-[#E5E5E5] last:border-b-0 flex gap-4" data-id="element-1964">
              {/* Image */}
              {image && <div className="w-20 h-20 shrink-0 flex items-center justify-center" data-id="element-1965">
                  <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-1966" />
                </div>}

              {/* Details */}
              <div className="flex-1 min-w-0" data-id="element-1967">
                <p className="text-sm sm:text-base font-bold text-[#111111] leading-snug" data-id="element-1968">
                  {item.name}
                </p>
                <p className="text-sm text-tealslate font-medium mt-1" data-id="element-1969">
                  In Stock
                </p>
                <p className="text-xs font-bold uppercase tracking-wider text-[#9A9A90] mt-1" data-id="element-1970">
                  {item.type === 'book' ? 'Digital Book' : 'Supplement'} · Qty{' '}
                  {item.quantity}
                </p>
              </div>

              {/* Price */}
              <div className="shrink-0 text-right" data-id="element-1971">
                <p className="text-sm sm:text-base font-bold text-[#111111] whitespace-nowrap" data-id="element-1972">
                  {formatPrice(item.unitPrice * item.quantity)}
                </p>
              </div>
            </li>;
      })}
      </ul>
      <div className="px-6 py-5 border-t border-[#E5E5E5] text-right" data-id="element-1973">
        <p className="text-lg text-[#111111]" data-id="element-1974">
          Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'}):{' '}
          <span className="font-bold" data-id="element-1975">{formatPrice(subtotal)}</span>
        </p>
        <p className="text-sm text-[#5A5A5A] mt-2" data-id="element-1976">
          Shipping and tax calculated at payment.
        </p>
      </div>
    </div>;
}